import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Search, Zap, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { useBulkPlanAssigner } from '@/hooks/useBulkPlanAssigner';
import { usePlans } from '@/hooks/usePlans';

const bulkPlanAssignerSchema = z.object({
  channelNames: z.string().min(1, 'Please enter at least one channel name'),
  planId: z.number().min(1, 'Please select a plan')
});

type BulkPlanAssignerFormData = z.infer<typeof bulkPlanAssignerSchema>;

export function BulkPlanAssignerPage() {
  const navigate = useNavigate();
  const { providerId, versionId, regionId } = useParams();
  const [isSearched, setIsSearched] = useState(false);
  const [assignmentProgress, setAssignmentProgress] = useState(0);

  // Parse URL parameters
  const providerIdNum = Number(providerId);
  const versionIdNum = Number(versionId);
  const regionIdNum = regionId ? Number(regionId) : null;

  // Get plans for this provider/version
  const { data: plans = [] } = usePlans(providerIdNum, versionIdNum);

  // Use the bulk plan assigner hook
  const {
    matchResult,
    isMatching,
    isAssigning,
    matchChannels,
    assignPlans,
    hasMatches
  } = useBulkPlanAssigner({
    providerId: providerIdNum,
    versionId: versionIdNum,
    regionId: regionIdNum,
    onSuccess: () => {
      // Navigate back to channels page after success
      const backPath = regionId 
        ? `/providers/${providerId}/regions/${regionId}/versions/${versionId}/dashboard`
        : `/providers/${providerId}/versions/${versionId}/dashboard`;
      navigate(backPath);
    }
  });

  const form = useForm<BulkPlanAssignerFormData>({
    resolver: zodResolver(bulkPlanAssignerSchema),
    defaultValues: {
      channelNames: '',
      planId: 0
    }
  });

  const watchedPlanId = form.watch('planId');
  const selectedPlan = plans.find(p => p.id === watchedPlanId);

  const handleSearch = useCallback(async () => {
    const channelNames = form.getValues('channelNames');
    if (!channelNames.trim()) return;
    
    await matchChannels(channelNames);
    setIsSearched(true);
  }, [matchChannels, form]);

  const handleAssignPlans = useCallback(async (data: BulkPlanAssignerFormData) => {
    if (!hasMatches) return;

    // Simulate progress updates for user feedback
    setAssignmentProgress(0);
    const progressInterval = setInterval(() => {
      setAssignmentProgress(prev => Math.min(prev + 10, 90));
    }, 100);

    try {
      await assignPlans(data.planId);
      clearInterval(progressInterval);
      setAssignmentProgress(100);
    } catch (error) {
      clearInterval(progressInterval);
      setAssignmentProgress(0);
    }
  }, [hasMatches, assignPlans]);

  const handleBack = () => {
    const backPath = regionId 
      ? `/providers/${providerId}/regions/${regionId}/versions/${versionId}/dashboard`
      : `/providers/${providerId}/versions/${versionId}/dashboard`;
    navigate(backPath);
  };

  const getStatusIcon = (type: 'exact' | 'fuzzy' | 'notFound') => {
    switch (type) {
      case 'exact':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'fuzzy':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'notFound':
        return <XCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getStatusBadge = (type: 'exact' | 'fuzzy' | 'notFound', similarity?: number) => {
    switch (type) {
      case 'exact':
        return <Badge className="bg-green-100 text-green-800">Exact Match</Badge>;
      case 'fuzzy':
        return <Badge className="bg-yellow-100 text-yellow-800">
          Fuzzy Match ({Math.round((similarity || 0) * 100)}%)
        </Badge>;
      case 'notFound':
        return <Badge variant="destructive">Not Found</Badge>;
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={handleBack} className="p-2">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6" />
            Bulk Plan Assigner
          </h1>
          <p className="text-muted-foreground">
            Paste channel names and assign them to a plan in bulk. Supports comma-separated or line-separated names.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Input Form */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Channel Input</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleAssignPlans)} className="space-y-6">
                {/* Channel Names Input */}
                <FormField
                  control={form.control}
                  name="channelNames"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Channel Names</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={`Enter channel names (one per line or comma-separated):

ESPN
CNN, Fox News
Discovery Channel
National Geographic
...`}
                          className="min-h-[200px] font-mono text-sm"
                        />
                      </FormControl>
                      <FormDescription>
                        Paste channel names separated by commas or new lines.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Search Button */}
                <Button
                  type="button"
                  onClick={handleSearch}
                  disabled={isMatching || !form.watch('channelNames').trim()}
                  className="w-full"
                  variant="outline"
                >
                  {isMatching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Search Channels
                    </>
                  )}
                </Button>

                {/* Plan Selection */}
                {isSearched && hasMatches && (
                  <FormField
                    control={form.control}
                    name="planId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Assign to Plan</FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            const numValue = parseInt(value);
                            field.onChange(numValue);
                          }} 
                          value={field.value?.toString() || ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a plan to assign" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {plans.map((plan) => (
                              <SelectItem key={plan.id} value={plan.id.toString()}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{
                                      backgroundColor: plan.color_index ? `hsl(${plan.color_index * 12}, 70%, 50%)` : '#94a3b8'
                                    }}
                                  />
                                  {plan.name}
                                  {plan.monthly_price && (
                                    <span className="text-muted-foreground">
                                      (${plan.monthly_price}/mo)
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Assignment Progress */}
                {isAssigning && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                      <span className="text-sm">Assigning plans to channels...</span>
                    </div>
                    <Progress value={assignmentProgress} className="w-full" />
                  </div>
                )}

                {/* Assignment Button */}
                {isSearched && hasMatches && selectedPlan && (
                  <div className="space-y-4">
                    <div className="text-sm text-muted-foreground">
                      Ready to assign <strong>{selectedPlan.name}</strong> to{' '}
                      <strong>{matchResult?.totalFound}</strong> channels
                    </div>
                    <Button
                      type="submit"
                      disabled={!hasMatches || !selectedPlan || isAssigning}
                      className="w-full"
                    >
                      {isAssigning ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          Assigning...
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4 mr-2" />
                          Assign Plans
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Debug info - remove this later */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="text-xs text-gray-500 p-2 bg-gray-100 rounded">
                    Debug: isSearched={isSearched ? 'true' : 'false'}, hasMatches={hasMatches ? 'true' : 'false'}, watchedPlanId={watchedPlanId}, selectedPlan={selectedPlan ? selectedPlan.name : 'none'}, totalFound={matchResult?.totalFound || 0}
                  </div>
                )}
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Right Side - Results Table */}
        {isSearched && matchResult && (
          <Card>
            <CardHeader>
              <CardTitle>Match Results</CardTitle>
              <div className="flex gap-4 text-sm">
                <span className="text-green-600">
                  {matchResult.exact.length} Exact
                </span>
                <span className="text-yellow-600">
                  {matchResult.fuzzy.length} Fuzzy
                </span>
                <span className="text-red-600">
                  {matchResult.notFound.length} Not Found
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-[600px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Input Name</TableHead>
                      <TableHead>Matched Channel</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Exact Matches */}
                    {matchResult.exact.map((match, index) => (
                      <TableRow key={`exact-${index}`}>
                        <TableCell className="font-mono text-sm">
                          "{match.originalInput}"
                        </TableCell>
                        <TableCell className="font-medium">
                          {match.name}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getStatusIcon('exact')}
                            {getStatusBadge('exact')}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Fuzzy Matches */}
                    {matchResult.fuzzy.map((match, index) => (
                      <TableRow key={`fuzzy-${index}`}>
                        <TableCell className="font-mono text-sm">
                          "{match.originalInput}"
                        </TableCell>
                        <TableCell className="font-medium">
                          {match.name}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getStatusIcon('fuzzy')}
                            {getStatusBadge('fuzzy', match.similarity)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Not Found */}
                    {matchResult.notFound.map((name, index) => (
                      <TableRow key={`notfound-${index}`}>
                        <TableCell className="font-mono text-sm">
                          "{name}"
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          No match found
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getStatusIcon('notFound')}
                            {getStatusBadge('notFound')}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}